import {Pagination} from '@/components';
import type {PaginationProps} from '@/components/common/Pagination';
import {TableSkeleton} from '@/components/Table/TableSkeleton';

import BusinessFeesFilters from './BusinessFeesFilters';
import BusinessFeesTable from './BusinessFeesTable';
import useFeeList from './useFeeList';

/** Test hook prefix; the create action, form, table and rows namespace under it. */
const DATA_CY = 'merchant-fees';

const getEmptyFeesMessage = (rawFeeCount: number) =>
  rawFeeCount > 0
    ? "No organization-default fees found. All existing fees are currently scoped to specific merchants. See the Fees tab on each merchant's page."
    : 'No fees found.';

/**
 * Organization-default fee list. Extracted from this screen's `index.tsx` when
 * the screen became tabbed (Fees / Invoices); the body is unchanged apart from
 * the `Header`, which now lives on the tab shell so it renders once above both
 * tabs rather than inside each one.
 */
const FeesTab = () => {
  const {
    isLoading,
    fetchList,
    refetchCurrentPage,
    displayPage,
    pageInfo,
    list,
    rawFeeCount,
    isPageReset,
    isSearch,
    selectCurrentFilters,
    currentFilters,
    handleAutoComplete,
    clearFilters,
  } = useFeeList({isGlobal: true});

  const renderList = () => {
    if (isLoading) {
      return <TableSkeleton />;
    }

    if (!list.length) {
      return (
        <p
          data-cy={`${DATA_CY}-empty`}
          className="py-10 text-center text-gray-500 dark:text-gray-400"
        >
          {getEmptyFeesMessage(rawFeeCount)}
        </p>
      );
    }

    return (
      <BusinessFeesTable
        items={list}
        onFeeSaved={refetchCurrentPage}
        dataCy={DATA_CY}
      />
    );
  };

  return (
    <>
      <div className="w-full">
        <BusinessFeesFilters
          dataCy={`${DATA_CY}-create`}
          formDataCy={`${DATA_CY}-form`}
          handleAutoComplete={handleAutoComplete}
          currentFilters={currentFilters}
          selectCurrentFilters={selectCurrentFilters}
          clearFilters={clearFilters}
          onFeeSaved={refetchCurrentPage}
        />
      </div>

      <section className="pt-5 md:pt-5">{renderList()}</section>

      {list && list.length ? (
        <Pagination
          onPageChange={fetchList as PaginationProps['onPageChange']}
          total={pageInfo.total}
          isSearch={isSearch}
          isPageReset={isPageReset}
          currentPageOverride={displayPage}
        />
      ) : null}
    </>
  );
};

export default FeesTab;
